import { useState } from "react";
import { Check, Landmark } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useAccountStore } from "@/features/accounts/store/useAccountStore";
import AccountAvatar from "@/features/accounts/components/AccountAvatar";
import { useAppStore } from "../../store/useAppStore";

interface AccountEditorProps {
  transactionId: string;
  accountId: string | null | undefined;
}

/** Shows which account a transaction belongs to and lets the user change or clear it. */
export default function AccountEditor({
  transactionId,
  accountId,
}: AccountEditorProps) {
  const accounts = useAccountStore((s) => s.accounts);
  const setTransactionAccount = useAppStore((s) => s.setTransactionAccount);
  const [open, setOpen] = useState(false);

  // Nothing to pick until the user has an account.
  if (accounts.length === 0) return null;

  const current = accounts.find((a) => a.id === accountId);
  const options = accounts.filter((a) => a.isActive || a.id === accountId);

  async function select(next: string | null) {
    setOpen(false);
    if (next === (accountId ?? null)) return;
    try {
      await setTransactionAccount(transactionId, next);
    } catch {
      toast.error("Could not change the account");
    }
  }

  const optionClass = (selected: boolean) =>
    `w-full flex items-center gap-2 text-[12px] font-medium px-2.5 py-1.5 rounded-lg text-left transition-colors text-foreground ${
      selected
        ? "bg-[var(--color-primary-bg)]"
        : "bg-transparent hover:bg-[var(--surface-muted)]"
    }`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Change account"
          aria-label="Change account"
          className={`inline-flex min-w-0 max-w-[16rem] items-center gap-1 px-1.5 py-0.5 rounded-[5px] text-[10px] font-medium cursor-pointer transition-colors bg-[var(--surface-muted)] hover:bg-border ${
            current ? "text-muted-foreground" : "text-muted-foreground/70"
          }`}
        >
          {current ? (
            <AccountAvatar
              size="sm"
              type={current.type}
              bankCode={current.bankCode}
            />
          ) : (
            <Landmark className="w-3 h-3" />
          )}
          <span className="truncate">
            {current ? current.name : "No account"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-1.5 rounded-xl w-auto min-w-[168px] bg-card border-[0.5px] border-border shadow-popover"
        align="start"
      >
        <button
          type="button"
          onClick={() => select(null)}
          className={optionClass(!accountId)}
        >
          Unassigned
          {!accountId && <Check className="w-3.5 h-3.5 ml-auto text-primary" />}
        </button>
        {options.map((account) => (
          <button
            key={account.id}
            type="button"
            onClick={() => select(account.id)}
            className={optionClass(account.id === accountId)}
          >
            <AccountAvatar
              size="sm"
              type={account.type}
              bankCode={account.bankCode}
            />
            {account.name}
            {account.digits ? ` ••${account.digits}` : ""}
            {account.id === accountId && (
              <Check className="w-3.5 h-3.5 ml-auto text-primary" />
            )}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
